import _, {noop} from 'lodash';
import React, {FC, RefObject, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CollapsedItemStates} from '../../../../helpers/expansionTogglingHelpers';
import {ParentTagOverridesMap} from '../../../../helpers/visualCustomViewHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {SearchTagModel} from '../../../../models/api/searchTagModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {
  VisualWorkspaceFolderNestedItem,
  VisualWorkspaceFolderRootLabel,
  VisualWorkspaceTag,
} from '../../../../models/api/visualWorkspaceModel';
import {
  WorkspaceFolderNestedLabelsEnum,
  WorkspaceFolderRootLabelsEnum,
  WorkspaceItemTypesEnum,
} from '../../../../models/api/workspaceModel';
import {
  WorkspaceDropReorderTagHandler,
  WorkspaceTagDropNestHandler,
} from '../../../../store/data/workspace/reorder/types';
import {TagModelMap} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {NavigationLineLabel} from '../../../common/navigation/navigationLineLabel';
import {NavigationLineStandardIcon} from '../../../common/navigation/navigationLineStandardIcon';
import {WorkspaceDragSourceTypesEnum, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {WorkspacesEditorPickTagsDropdownContainer} from '../dropdowns/workspacesEditorPickTagsDropdownContainer';
import {WorkspacesEditorTeammateMoreDropdown} from '../dropdowns/workspacesEditorTeammateMoreDropdown';
import {WorkspaceHeader} from '../header/workspaceHeader';
import {WorkspaceLineDropTarget} from '../line/workspaceLineDropTarget';
import {WorkspaceLineMoreContainer} from '../line/workspaceLineMoreContainer';
import {TeammateWorkspaceEditorCustomViewsContainer} from '../teammateWorkspace/TeammateWorkspaceEditorCustomViewsContainer';
import {TeammateWorkspaceStarItemContainer} from '../teammateWorkspace/teammateWorkspaceStarItemContainer';
import {Workspace} from '../workspace';
import {WorkspaceGenericItemContainer} from '../workspaceGenericItemContainer';
import {WorkspaceInboxItemContainer} from '../workspaceInboxItemContainer';
import {
  ToggleExpandItemHandler,
  ToggleExpandTagHandler,
  workspacesEditorDropdownWidth,
} from './context/types';
import {
  TagCreatedHandler,
  ToggleDropdownTagHandler,
  ToggleTagHandler,
} from './context/workspacesEditorContext';

const defaultCustomViewExpandedState = true;

/*
 * Style.
 */

export const StyledEditModeLabelDiv = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.interactiveText};
  font-weight: ${fontWeights.medium};
`;

/*
 * Props.
 */

export interface TeammateWorkspaceEditorProps {
  panelId: InboxesPanelId;
  /** Private inboxes the user has access to. */
  inboxes: ReadonlyArray<InboxModel>;
  visualWorkspaceRootFolders: ReadonlyArray<VisualWorkspaceFolderRootLabel>;
  visualWorkspaceNestedFolders: ReadonlyArray<VisualWorkspaceFolderNestedItem>;
  /** Handler for toggling label items. */
  onToggleRootFolder: HandlerOf<WorkspaceFolderRootLabelsEnum>;
  /** Our user's namespace. */
  userNamespace: string;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Whether this Inbox section is collapsed or not. */
  isInboxExpanded: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpand: Handler;
  /** Event handler to expand/collapse the inbox section. */
  onToggleInboxExpand: Handler;
  /** Our user's starred tag id. */
  userStarredTagId: number;
  /** Hash map of workspace item expansion toggle states. */
  collapsedItems: CollapsedItemStates;
  /** Handler for toggling the expanded/collapsed state of section items. */
  onToggleExpandSection: ToggleExpandItemHandler;
  /** Handler for toggling the expanded/collapsed state of tag items. */
  onToggleExpandTag: ToggleExpandTagHandler;
  /** Handler for toggling tag items in P1. */
  onToggleTag: ToggleTagHandler;
  /** Handler for toggling tag items in private and team tag search modals. */
  onToggleDropdownTag: ToggleDropdownTagHandler;
  /** Map of id to tagModel so we can display them in P1. */
  tagModelMap: TagModelMap;
  /** Represents both private and team tags that appear in this workspace. */
  visualWorkspaceTags: ReadonlyArray<VisualWorkspaceTag>;
  /** Whether P1 is alphabetized in the VisualWorkspaceModel. */
  isAlphabetized: boolean;
  /** Teams we display team tags for. */
  teams: TeamModel[];
  parentTagOverridesMap: ParentTagOverridesMap;
  onDropNestCustomView: WorkspaceTagDropNestHandler;
  onDropReorderCustomView: WorkspaceDropReorderTagHandler;
  onTagCreated: TagCreatedHandler;
  onDropReorderNestedFolder: WorkspaceItemDropHandler;
  canAddTeamTags: boolean;
}

/*
 * Component.
 */

/**
 * This component is adapted from <TeammateWorkspace /> but for use in <WorkspacesEditor />.
 * @see TeammateWorkspace
 */
export const TeammateWorkspaceEditor: FC<TeammateWorkspaceEditorProps> = (props) => {
  const [shouldShowPrivateTagsDropdown, setShouldShowPrivateTagsDropdown] = useState<boolean>(false);
  const [shouldShowTeamTagsDropdown, setShouldShowTeamTagsDropdown] = useState<boolean>(false);

  const [privateTagsDropdownSelectedIds, setPrivateTagsDropdownSelectedIds] = useState<number[]>([]);
  const [teamTagsDropdownSelectedIds, setTeamTagsDropdownSelectedIds] = useState<number[]>([]);

  const {
    panelId,
    isExpanded,
    onToggleExpand,
    visualWorkspaceTags,
    tagModelMap,
    teams,
    onTagCreated,
    onToggleDropdownTag,
  } = props;

  const onPrivateTagsClick = () => setShouldShowPrivateTagsDropdown(true);
  const onPrivateTagsDismiss = () => {
    setShouldShowPrivateTagsDropdown(false);
    setPrivateTagsDropdownSelectedIds([]);
  };
  const onToggleDropdownPrivateTag = (
    searchTag: SearchTagModel,
    parentTagIdsToAdd: number[],
    childTagIdsToRemove: number[],
  ) => {
    onToggleDropdownTag(searchTag, parentTagIdsToAdd, childTagIdsToRemove);
    setPrivateTagsDropdownSelectedIds([...new Set(privateTagsDropdownSelectedIds).add(searchTag[0].id)]);
  };
  const addPrivateTagsRef = useRef<HTMLDivElement>(null);

  const onTeamTagsClick = () => setShouldShowTeamTagsDropdown(true);
  const onTeamTagsDismiss = () => {
    setShouldShowTeamTagsDropdown(false);
    setTeamTagsDropdownSelectedIds([]);
  };
  const onToggleDropdownTeamTag = (
    searchTag: SearchTagModel,
    parentTagIdsToAdd: number[],
    childTagIdsToRemove: number[],
  ) => {
    onToggleDropdownTag(searchTag, parentTagIdsToAdd, childTagIdsToRemove);
    setTeamTagsDropdownSelectedIds([...new Set(teamTagsDropdownSelectedIds).add(searchTag[0].id)]);
  };

  const addTeamTagsRef = useRef<HTMLDivElement>(null);

  return (
    <Workspace isExpanded={isExpanded}>
      {/* Header. */}
      <WorkspaceHeader
        panelId={panelId}
        interactionId="teammate-editor"
        isExpanded={isExpanded}
        onToggleExpandClick={onToggleExpand}
        isEditingWorkspace
      >
        <Trans id="workspace-teammate-title">Me</Trans>
      </WorkspaceHeader>
      {/* Workspace content. */}
      {maybeRenderExpandedContent({
        props,
        addPrivateTagsRef,
        addTeamTagsRef,
        onPrivateTagsClick,
        onTeamTagsClick,
        shouldShowPrivateTagsDropdown,
        shouldShowTeamTagsDropdown,
      })}
      {maybeRenderAddPrivateTagsDropdown({
        anchor: addPrivateTagsRef,
        shouldShowTagsDropdown: shouldShowPrivateTagsDropdown,
        visualWorkspaceTags,
        onToggleDropdownTag: onToggleDropdownPrivateTag,
        onDismiss: onPrivateTagsDismiss,
        tagModelMap,
        onTagCreated,
        tagDropdownSelectedIds: privateTagsDropdownSelectedIds,
      })}
      {maybeRenderAddTeamTagsDropdown({
        anchor: addTeamTagsRef,
        shouldShowTagsDropdown: shouldShowTeamTagsDropdown,
        visualWorkspaceTags,
        onToggleDropdownTag: onToggleDropdownTeamTag,
        onDismiss: onTeamTagsDismiss,
        tagModelMap,
        teams,
        onTagCreated,
        tagDropdownSelectedIds: teamTagsDropdownSelectedIds,
      })}
    </Workspace>
  );
};

/*
 * Helpers.
 */

interface ExpandedContentArgs {
  props: TeammateWorkspaceEditorProps;
  addPrivateTagsRef: React.RefObject<HTMLDivElement>;
  addTeamTagsRef: React.RefObject<HTMLDivElement>;
  onPrivateTagsClick: Handler;
  onTeamTagsClick: Handler;
  shouldShowPrivateTagsDropdown: boolean;
  shouldShowTeamTagsDropdown: boolean;
}

function maybeRenderExpandedContent(args: ExpandedContentArgs) {
  const {
    props,
    addPrivateTagsRef,
    addTeamTagsRef,
    onPrivateTagsClick,
    onTeamTagsClick,
    shouldShowPrivateTagsDropdown,
    shouldShowTeamTagsDropdown,
  } = args;
  const {
    panelId,
    isExpanded,
    isInboxExpanded,
    onToggleInboxExpand,
    onToggleExpandTag,
    parentTagOverridesMap,
    onDropNestCustomView,
    onDropReorderCustomView,
    collapsedItems,
    visualWorkspaceTags,
    tagModelMap,
    onToggleTag,
    canAddTeamTags,
  } = props;
  if (!isExpanded) {
    return null;
  }

  return (
    <>
      {
        <>
          {/* Section Header. */}
          <WorkspaceGenericItemContainer
            shouldHideMoreMenu
            canBeHidden={false}
            shouldAlphabetizeWorkspace={false}
            workspaceDragItem={undefined}
            iconName="inboxSmall"
            selectedIconName="inboxFilledSmall"
            {...props}
            isExpanded={isInboxExpanded}
            onToggleExpandClick={onToggleInboxExpand}
          >
            <Trans id="workspace-inbox">Inbox</Trans>
          </WorkspaceGenericItemContainer>

          {/* Content of the "Inbox" section. */}
          {maybeRenderNestedFolders(props)}
        </>
      }

      {/* Note: Starred, Drafts, and Sent are never re-ordered. */}
      {renderRootFolders(props)}

      {/* Private and Team Tags. */}
      {
        <TeammateWorkspaceEditorCustomViewsContainer
          panelId={panelId}
          shouldAlphabetizeWorkspace={props.isAlphabetized}
          isEditingWorkspace
          visualWorkspaceTags={visualWorkspaceTags}
          collapsedItems={collapsedItems}
          onCustomViewToggleExpandInEditor={(vcv) =>
            onToggleExpandTag({
              tagId: vcv.tag.id,
              defaultExpandedState: defaultCustomViewExpandedState,
            })
          }
          tagModelMap={tagModelMap}
          onToggleTag={onToggleTag}
          parentTagOverridesMap={parentTagOverridesMap}
          onDropNestCustomView={onDropNestCustomView}
          onDropReorderCustomView={onDropReorderCustomView}
        />
      }

      {renderAddPrivateTagsButton(onPrivateTagsClick, addPrivateTagsRef, shouldShowPrivateTagsDropdown)}
      {canAddTeamTags && renderAddTeamTagsButton(onTeamTagsClick, addTeamTagsRef, shouldShowTeamTagsDropdown)}
    </>
  );
}

function maybeRenderNestedFolders(props: TeammateWorkspaceEditorProps) {
  const {isInboxExpanded, visualWorkspaceNestedFolders, panelId, inboxes, onDropReorderNestedFolder} = props;

  if (!isInboxExpanded) {
    return null;
  }

  const lastIndex = visualWorkspaceNestedFolders.length - 1;

  // Items nested under "Inbox" are of two types: inbox or label.
  return _(visualWorkspaceNestedFolders)
    .map((item, index) => {
      const isLast = index === lastIndex;
      switch (item.type) {
        case WorkspaceItemTypesEnum.INBOX:
          return renderNestedInbox({
            panelId,
            inboxId: item.id,
            inboxes,
            isLast,
            onDropReorderNestedFolder,
          });

        case WorkspaceItemTypesEnum.LABEL:
          return renderNestedLabel({
            panelId,
            isLast,
            name: item.id,
            onDropReorderNestedFolder,
          });

        default:
          return null;
      }
    })
    .value();
}

interface RenderInboxArgs {
  panelId: InboxesPanelId;
  inboxId: number;
  inboxes: readonly InboxModel[];
  isLast: boolean;
  onDropReorderNestedFolder: WorkspaceItemDropHandler;
}

function renderNestedInbox({panelId, inboxId, inboxes, isLast, onDropReorderNestedFolder}: RenderInboxArgs) {
  const inbox = inboxes.find(({id}) => id === inboxId);
  if (!inbox) {
    return null;
  }
  return (
    <WorkspaceInboxItemContainer
      key={inboxId}
      panelId={panelId}
      inbox={inbox}
      indentLevel={1}
      isChecked
      isEditorCheckboxDisabled
      isEditingWorkspace
      shouldHideMoreMenu
      canBeHidden={false}
      shouldAlphabetizeWorkspace={false}
      shouldShowRefinedResultsIndicator={false}
      workspaceDragItem={{
        type: WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER,
        id: inbox.id,
        isLast,
      }}
      onWorkspaceItemDrop={onDropReorderNestedFolder}
      hasEditPermission={false}
      selectedRefinement={undefined}
    />
  );
}

interface RenderLabelArgs {
  panelId: InboxesPanelId;
  name: WorkspaceFolderNestedLabelsEnum;
  isLast: boolean;
  onDropReorderNestedFolder: WorkspaceItemDropHandler;
}

function renderNestedLabel({panelId, name, isLast, onDropReorderNestedFolder}: RenderLabelArgs) {
  switch (name) {
    case WorkspaceFolderNestedLabelsEnum.ASSIGNED_TO_ME:
      return (
        <WorkspaceGenericItemContainer
          key={name}
          panelId={panelId}
          iconName="assignSmall"
          selectedIconName="assignFilledSmall"
          indentLevel={1}
          isEditorCheckboxDisabled
          isEditingWorkspace
          isChecked
          shouldHideMoreMenu
          canBeHidden={false}
          shouldAlphabetizeWorkspace={false}
          workspaceDragItem={{
            type: WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER,
            id: WorkspaceFolderNestedLabelsEnum.ASSIGNED_TO_ME,
            isLast,
          }}
          onWorkspaceItemDrop={onDropReorderNestedFolder}
        >
          <Trans id="workspace-assigned-to-me">Assigned to me</Trans>
        </WorkspaceGenericItemContainer>
      );

    case WorkspaceFolderNestedLabelsEnum.SHARED_WITH_ME:
      return (
        <WorkspaceGenericItemContainer
          key={name}
          panelId={panelId}
          iconName="teammatesSmall"
          selectedIconName="teammatesFilledSmall"
          indentLevel={1}
          isEditorCheckboxDisabled
          isEditingWorkspace
          isChecked
          shouldHideMoreMenu
          canBeHidden={false}
          shouldAlphabetizeWorkspace={false}
          workspaceDragItem={{
            type: WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER,
            id: WorkspaceFolderNestedLabelsEnum.SHARED_WITH_ME,
            isLast,
          }}
          onWorkspaceItemDrop={onDropReorderNestedFolder}
        >
          <Trans id="workspace-sharedwithme">Shared with me</Trans>
        </WorkspaceGenericItemContainer>
      );

    case WorkspaceFolderNestedLabelsEnum.DISCUSSIONS:
      return (
        <WorkspaceGenericItemContainer
          key={name}
          panelId={panelId}
          iconName="discussionSmall"
          selectedIconName="discussionFilledSmall"
          indentLevel={1}
          isEditorCheckboxDisabled
          isEditingWorkspace
          isChecked
          shouldHideMoreMenu
          canBeHidden
          shouldAlphabetizeWorkspace={false}
          workspaceDragItem={{
            type: WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER,
            id: WorkspaceFolderNestedLabelsEnum.DISCUSSIONS,
            isLast,
          }}
          onWorkspaceItemDrop={onDropReorderNestedFolder}
        >
          <Trans id="workspace-discussions">Discussions</Trans>
        </WorkspaceGenericItemContainer>
      );

    default:
      return null;
  }
}

function renderAddPrivateTagsButton(
  onPrivateTagsClick: Handler,
  addPrivateTagsRef: React.RefObject<HTMLDivElement>,
  shouldShowPrivateTagsDropdown: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <div ref={addPrivateTagsRef}>
          <WorkspaceLineDropTarget
            indentLevel={0}
            onClick={onPrivateTagsClick}
            shouldAlphabetizeWorkspace={false}
            workspaceDragItem={undefined}
            isSelected={shouldShowPrivateTagsDropdown}
          >
            {<NavigationLineStandardIcon iconName="lockSmall" color={theme.palette.blue.shade40} />}
            <NavigationLineLabel>
              <StyledEditModeLabelDiv>
                <Trans id="edit-mode-add-private-tags">Add private tags</Trans>
              </StyledEditModeLabelDiv>
            </NavigationLineLabel>
            <WorkspaceLineMoreContainer isEditingWorkspaceButton />
          </WorkspaceLineDropTarget>
        </div>
      )}
    </UserThemeConsumer>
  );
}

function renderAddTeamTagsButton(
  onTeamTagsClick: Handler,
  addTeamTagsRef: React.RefObject<HTMLDivElement>,
  shouldShowTeamTagsDropdown: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <div ref={addTeamTagsRef}>
          <WorkspaceLineDropTarget
            indentLevel={0}
            onClick={onTeamTagsClick}
            shouldAlphabetizeWorkspace={false}
            workspaceDragItem={undefined}
            isSelected={shouldShowTeamTagsDropdown}
          >
            {<NavigationLineStandardIcon iconName="tagSmall" color={theme.palette.blue.shade40} />}
            <NavigationLineLabel>
              <StyledEditModeLabelDiv>
                <Trans id="edit-mode-add-team-tags">Add team tags</Trans>
              </StyledEditModeLabelDiv>
            </NavigationLineLabel>
            <WorkspaceLineMoreContainer isEditingWorkspaceButton />
          </WorkspaceLineDropTarget>
        </div>
      )}
    </UserThemeConsumer>
  );
}

interface AddPrivateTagsArgs {
  anchor: RefObject<HTMLDivElement>;
  shouldShowTagsDropdown: boolean;
  visualWorkspaceTags: ReadonlyArray<VisualWorkspaceTag>;
  onToggleDropdownTag: ToggleDropdownTagHandler;
  onDismiss: Handler;
  tagModelMap: TagModelMap;
  onTagCreated: TagCreatedHandler;
  tagDropdownSelectedIds: number[];
}

interface AddTeamTagsArgs extends AddPrivateTagsArgs {
  teams: TeamModel[];
}

function maybeRenderAddPrivateTagsDropdown(args: AddPrivateTagsArgs) {
  const {
    anchor,
    shouldShowTagsDropdown,
    visualWorkspaceTags,
    onToggleDropdownTag,
    onDismiss,
    tagModelMap,
    onTagCreated,
    tagDropdownSelectedIds,
  } = args;
  return (
    shouldShowTagsDropdown && (
      <WorkspacesEditorPickTagsDropdownContainer
        anchor={anchor}
        backButtonLabel={undefined}
        onBackButtonClick={noop}
        placement="right-start"
        onRequestClose={onDismiss}
        width={workspacesEditorDropdownWidth}
        visualWorkspaceTags={visualWorkspaceTags}
        onToggleTag={onToggleDropdownTag}
        tagModelMap={tagModelMap}
        onTagCreated={onTagCreated}
        tagDropdownSelectedIds={tagDropdownSelectedIds}
      />
    )
  );
}

function maybeRenderAddTeamTagsDropdown(args: AddTeamTagsArgs) {
  const {
    anchor,
    shouldShowTagsDropdown,
    visualWorkspaceTags,
    onToggleDropdownTag,
    onDismiss,
    tagModelMap,
    teams,
    onTagCreated,
    tagDropdownSelectedIds,
  } = args;
  return (
    shouldShowTagsDropdown && (
      <WorkspacesEditorTeammateMoreDropdown
        teams={teams}
        anchor={anchor}
        placement="right-start"
        onRequestClose={onDismiss}
        visualWorkspaceTags={visualWorkspaceTags}
        onToggleTeamTag={onToggleDropdownTag}
        tagModelMap={tagModelMap}
        width={workspacesEditorDropdownWidth}
        onTagCreated={onTagCreated}
        tagDropdownSelectedIds={tagDropdownSelectedIds}
      />
    )
  );
}

function renderRootFolders(props: TeammateWorkspaceEditorProps) {
  const {panelId, userStarredTagId, userNamespace, visualWorkspaceRootFolders} = props;

  return _(visualWorkspaceRootFolders)
    .map((folder) => {
      switch (folder.id) {
        case WorkspaceFolderRootLabelsEnum.STARRED:
          return (
            <TeammateWorkspaceStarItemContainer
              key={folder.id}
              shouldHideMoreMenu
              canBeHidden
              panelId={panelId}
              starredTagId={userStarredTagId}
              namespace={userNamespace}
              isEditingWorkspace
              isChecked={folder.isSelected}
              onClick={() => props.onToggleRootFolder(WorkspaceFolderRootLabelsEnum.STARRED)}
            />
          );

        case WorkspaceFolderRootLabelsEnum.DRAFTS:
          return (
            <WorkspaceGenericItemContainer
              key={folder.id}
              shouldHideMoreMenu
              canBeHidden
              shouldAlphabetizeWorkspace={false}
              workspaceDragItem={undefined}
              panelId={panelId}
              iconName="draftSmall"
              selectedIconName="draftFilledSmall"
              isEditingWorkspace
              isChecked={folder.isSelected}
              onClick={() => props.onToggleRootFolder(WorkspaceFolderRootLabelsEnum.DRAFTS)}
            >
              <Trans id="workspace-drafts">Drafts</Trans>
            </WorkspaceGenericItemContainer>
          );

        case WorkspaceFolderRootLabelsEnum.SENT:
          return (
            <WorkspaceGenericItemContainer
              key={folder.id}
              shouldHideMoreMenu
              canBeHidden
              shouldAlphabetizeWorkspace={false}
              workspaceDragItem={undefined}
              panelId={panelId}
              iconName="sendSmall"
              selectedIconName="sendFilledSmall"
              isEditingWorkspace
              isChecked={folder.isSelected}
              onClick={() => props.onToggleRootFolder(WorkspaceFolderRootLabelsEnum.SENT)}
            >
              <Trans id="workspace-sent">Sent</Trans>
            </WorkspaceGenericItemContainer>
          );

        default:
          return null;
      }
    })
    .compact()
    .value();
}
